"use client";

import { useSession } from "@/context/SessionContext";
import {
    subscriptionFeatureAction,
    CycleUsageType,
    SubscriptionPlanInfoType,
} from "@/utils/serverActions/subscriptions/subscriptionActions";
import { callQuery } from "@/utils/queries/callQuery";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

// === Query Keys ===
export const queryKeys = {
  subscriptionPlanInfo: (companyId: number) => ["subscriptionPlanInfo", companyId] as const,
  usageByCycle: (companyId: number) => ["usageByCycle", companyId] as const,
  subscriptionFeature: (companyId: number, featureKey: string) =>
    ["subscriptionFeature", companyId, featureKey] as const,
  employeeLimitCheck: (companyId: number) => ["employeeLimitCheck", companyId] as const, // Added for employeeLimitCheck
};

// === Hook 1: Plan Info ===
export const useSubscriptionPlanInfo = () => {
  const { session } = useSession();
  const companyId = session?.companyId;

  return useQuery({
    queryKey: companyId ? queryKeys.subscriptionPlanInfo(companyId) : ["subscriptionPlanInfo", "no-id"],
    queryFn: async (): Promise<SubscriptionPlanInfoType> => {
      if (!companyId) throw new Error("No company ID");
      return await callQuery("subscriptionPlanInfo", { p_company_id: companyId });
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, 
    refetchOnWindowFocus: false,
  }) as UseQueryResult<SubscriptionPlanInfoType, Error>;
};

// === Hook 2: Usage By Cycle ===
export const useUsageByCycle = () => {
  const { session } = useSession();
  const companyId = session?.companyId;

  return useQuery({
    queryKey: companyId ? queryKeys.usageByCycle(companyId) : ["usageByCycle", "no-id"],
    queryFn: async (): Promise<CycleUsageType[]> => {
      if (!companyId) return [];
      return await callQuery("usageByCycle", { p_company_id: companyId });
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  }) as UseQueryResult<CycleUsageType[], Error>;
};

// === Hook 3: Check Feature ===
export const useSubscriptionFeature = (featureKey: string) => {
  const { session } = useSession();
  const companyId = session?.companyId;

  return useQuery({
    queryKey: companyId ? queryKeys.subscriptionFeature(companyId, featureKey) : ["subscriptionFeature", "no-id", featureKey],
    queryFn: async (): Promise<boolean> => {
      if (!companyId) return false;
      return await subscriptionFeatureAction({ p_company_id: companyId, p_feature_key: featureKey });
    },
    enabled: !!companyId && !!featureKey,
    staleTime: 60 * 60 * 1000, // Features cambian poco, cache largo
  });
};

// === Hook 5: Employee Limit Check ===
const TOLERANCE = 1; // Allow 1 employee over the limit with warning

export type EmployeeLimitStatus =
  | "available"
  | "low_space"
  | "no_space"
  | "tolerance"
  | "over_tolerance";

export interface EmployeeLimitCheck {
  status: EmployeeLimitStatus;
  availableSpaces: number;
  activeEmployees: number;
  employeeLimit: number | null;
  canAdd: boolean;
  requiresWarning: boolean;
  message: string;
}

export function useEmployeeLimitCheck(): EmployeeLimitCheck {
  const { data: planInfo } = useSubscriptionPlanInfo(); // Reference internal hook

  if (!planInfo) {
    return {
      status: "available",
      availableSpaces: Infinity,
      activeEmployees: 0,
      employeeLimit: null,
      canAdd: true,
      requiresWarning: false,
      message: "Cargando información del plan...",
    };
  }

  const employeeLimit = planInfo.limits?.employees ?? null;
  const activeEmployees = planInfo.active_employees ?? 0;

  // If no limit (Pro plan), allow unlimited
  if (employeeLimit === null) {
    return {
      status: "available",
      availableSpaces: Infinity,
      activeEmployees,
      employeeLimit: null,
      canAdd: true,
      requiresWarning: false,
      message: "Plan Pro - Sin límite de empleados",
    };
  }

  const availableSpaces = employeeLimit - activeEmployees;
  const toleranceUsed = activeEmployees > employeeLimit;
  const overTolerance = activeEmployees > employeeLimit + TOLERANCE;

  let status: EmployeeLimitStatus;
  let canAdd: boolean;
  let requiresWarning: boolean;
  let message: string;

  if (overTolerance) {
    status = "over_tolerance";
    canAdd = false;
    requiresWarning = false;
    message = `Has excedido el límite de empleados. No puedes agregar más empleados hasta que reduzcas el número actual (${activeEmployees}/${employeeLimit}).`;
  } else if (toleranceUsed) {
    status = "tolerance";
    canAdd = false;
    requiresWarning = false;
    message = `Has superado el límite de empleados. Te hemos regalado un espacio adicional, pero no puedes agregar más empleados hasta que reduzcas el número actual o actualices al plan Pro.`;
  } else if (availableSpaces === 0) {
    status = "no_space";
    canAdd = false;
    requiresWarning = false;
    message = `Has alcanzado el límite. Actualiza o mejora tu plan para aumentar la capacidad.`;
  } else if (availableSpaces <= 2) {
    status = "low_space";
    canAdd = true;
    requiresWarning = true;
    message = `Solo hay ${availableSpaces} espacio${availableSpaces !== 1 ? "s" : ""} disponible${availableSpaces !== 1 ? "s" : ""}. Considera actualizar al plan Pro.`;
  } else {
    status = "available";
    canAdd = true;
    requiresWarning = false;
    message = `${availableSpaces} espacio${availableSpaces !== 1 ? "s" : ""} disponible${availableSpaces !== 1 ? "s" : ""}.`;
  }

  return {
    status,
    availableSpaces: Math.max(0, availableSpaces),
    activeEmployees,
    employeeLimit,
    canAdd,
    requiresWarning,
    message,
  };
}